import { useOktaAuth } from '@okta/okta-react';
import ky from 'ky';
import { createWithEqualityFn } from 'zustand/traditional';
import { persist } from 'zustand/middleware';
import { dateTimeReviver } from '@utils/dateTimeReviver';
import { Environment, getAppEnvironment, getOktaAuthConfig } from '../config';
import { shallow } from 'zustand/shallow';
import { HolidayMode } from '@theme/palettes/getPalette';
import { celebrate, OPEN_HOLIDAY_PREVIEW_EVENT } from '@components/holiday/celebrate';
import { getOrCreateSessionId } from '@utils/sessionId';

export const apiEnvByAppEnv = {
  [Environment.Local]: Environment.Local,
  [Environment.Dev]: Environment.Int,
  [Environment.Int]: Environment.Int,
  [Environment.QA]: Environment.QA,
  [Environment.UAT]: Environment.UAT,
  [Environment.SuppUAT]: Environment.SuppUAT,
  [Environment.Prod]: Environment.Prod,
  [Environment.DR]: Environment.DR,
  [Environment.Demo]: Environment.Demo,
};

export const appEnvsByOktaRoleEnv = {
  [Environment.Dev]: [Environment.Local, Environment.Dev, Environment.Int, Environment.QA],
  [Environment.UAT]: [Environment.UAT, Environment.SuppUAT, Environment.Demo],
  [Environment.Prod]: [Environment.Prod],
};

type DebugModeOptions = {
  showQuoteCodeNames: boolean;
  showQuoteFieldInformation: boolean;
  showExtraDetails: boolean;
};

type ApiEnvStore = {
  environment: Environment;
  debugMode: boolean;
  toggleDebugMode: (value: boolean) => void;
  debugModeOptions: DebugModeOptions;
  setEnvironment: (env: Environment) => void;
  setDebugModeOption: (key: string, value: boolean) => void;
  earlyHolidayMode?: HolidayMode | null;
  disableHolidayModeFlag: boolean;
  toggleDisableHolidayModeFlag: () => void;
  featureFlagOverrides: Record<string, boolean>;
  setFeatureFlagOverride: (flagName: string, enabled: boolean) => void;
  clearFeatureFlagOverrides: () => void;
};

export const EnablePaymentPortal = true;

export const useApiEnvStore = createWithEqualityFn<ApiEnvStore>()(
  persist(
    (set) => ({
      environment: getDefaultEnvironment(),
      debugMode: false,
      debugModeOptions: {
        showQuoteCodeNames: true,
        showQuoteFieldInformation: false,
        showExtraDetails: false,
      },
      toggleDebugMode: (value: boolean) =>
        set({
          debugMode: value,
        }),
      setEnvironment: (env) => set({ environment: env }),
      setDebugModeOption: (key, value) =>
        set((state) => ({
          debugModeOptions: { ...state.debugModeOptions, [key]: value },
        })),
      earlyHolidayMode: null,
      disableHolidayModeFlag: true,
      toggleDisableHolidayModeFlag: () => set((state) => ({ disableHolidayModeFlag: !state.disableHolidayModeFlag })),
      featureFlagOverrides: {},
      setFeatureFlagOverride: (flagName, enabled) =>
        set((state) => ({
          featureFlagOverrides: { ...state.featureFlagOverrides, [flagName]: enabled },
        })),
      clearFeatureFlagOverrides: () => set({ featureFlagOverrides: {} }),
    }),
    {
      name: 'lio-api-env-storage',
      version: 3,
      merge(persistedState: any, currentState) {
        const mergedState = { ...currentState, ...persistedState };

        return mergedState;
      },
    },
  ),
  shallow,
);

export function getDefaultEnvironment() {
  return apiEnvByAppEnv[getAppEnvironment()];
}

export const getApiDomain = (env: Environment) => {
  if (import.meta.env.MODE === 'test') return 'https://unit-test/';
  return env === Environment.Local ? 'http://localhost:3080' : `https://alb.api-${env}.mylioinsurance.com`;
};

const PUBLIC_ACCESS_APIS: { url: string | RegExp; method: string }[] = [
  { url: '/user', method: 'POST' },
  { url: '/quote-setup-preset', method: 'GET' },
  { url: /\/indication\/[^/]+\/summary/, method: 'GET' },
  { url: '/system-management/system-alert', method: 'GET' },
];

const isPublicApi = (url: string | undefined, method: string | undefined) =>
  PUBLIC_ACCESS_APIS.some(
    (api) => method === api.method && (typeof api.url === 'string' ? url?.includes(api.url) : api.url.test(url ?? '')),
  );

export const useClient = () => {
  const { authState } = useOktaAuth();
  const environment = useApiEnvStore((state) => state.environment, shallow);

  return ky.create({
    prefixUrl: getApiDomain(environment),
    headers: {
      authorization: authState?.accessToken?.accessToken ? `Bearer ${authState.accessToken.accessToken}` : undefined,
    },
    timeout: 30000,
    parseJson: (text) => JSON.parse(text, dateTimeReviver),
    hooks: {
      beforeRequest: [
        (request) => {
          // Add session ID for correlation across multiple requests, but do not overwrite if already set
          if (!request.headers.has('X-Session-Id')) {
            request.headers.set('X-Session-Id', getOrCreateSessionId());
          }
        },
      ],
    },
    fetch: (input, init) => {
      if (input instanceof Request) {
        const url = input?.url;
        const method = input?.method;

        if (!authState?.accessToken?.accessToken && isPublicApi(url, method)) {
          return window.fetch(input, init);
        } else if (import.meta.env.MODE !== 'test' && !authState?.accessToken?.accessToken) {
          // this will prevent the calls from going out before we have an auth token/errors we are seeing on page load if refreshed
          return Promise.resolve(new Response(null, { status: 401, statusText: 'Unauthorized' }));
        }
      }

      return window.fetch(input, init);
    },
  });
};

function setEnvironment(env: Environment) {
  const previousOkta = getOktaAuthConfig(window.lio.getApiEnvironment());
  const newOkta = getOktaAuthConfig(env);

  if (previousOkta.issuer !== newOkta.issuer) {
    window.location.href = `/logout?env=${env}`;
  } else {
    useApiEnvStore.setState({ environment: env });
    window.location.reload();
  }
}

function _setEarlyHolidayAccess(holiday: HolidayMode) {
  useApiEnvStore.setState({ earlyHolidayMode: holiday });
  window.location.reload();
}

/**
 * Console-only testing helper: force a holiday on regardless of today's date, with holiday mode switched on.
 *   lio.previewHoliday()              - open a picker in the page (also opens for an unknown name)
 *   lio.previewHoliday('Christmas')   - preview one (reloads)
 *   lio.previewHoliday('off')         - back to normal (reloads)
 */
function previewHoliday(holiday?: HolidayMode | 'off') {
  const holidays = Object.values(HolidayMode);

  if (holiday === undefined || (holiday !== 'off' && !holidays.includes(holiday))) {
    window.dispatchEvent(new Event(OPEN_HOLIDAY_PREVIEW_EVENT));
    return;
  }
  if (holiday === 'off') {
    useApiEnvStore.setState({ earlyHolidayMode: null, disableHolidayModeFlag: true });
    window.location.reload();
    return;
  }
  useApiEnvStore.setState({ earlyHolidayMode: holiday, disableHolidayModeFlag: false });
  window.location.reload();
}

function setDebugMode(value: boolean) {
  useApiEnvStore.setState({
    debugMode: value,
    debugModeOptions: {
      showQuoteCodeNames: value,
      showQuoteFieldInformation: false,
      showExtraDetails: false,
    },
  });
}

// Ensure the lio namespace exists before attaching these helpers (this module
// may evaluate before the app-initialization side-effect import).
window.lio = window.lio ?? {};
window.lio.setApiEnvironment = setEnvironment;
window.lio.getApiEnvironment = () => useApiEnvStore.getState().environment;
window.lio.setDebugMode = setDebugMode;
window.lio.getHolidayModeFlag = () => useApiEnvStore.getState().disableHolidayModeFlag;
window.lio.getEarlyHolidayMode = () => useApiEnvStore.getState().earlyHolidayMode ?? null;
window.lio.setEarlyHolidayAccess = _setEarlyHolidayAccess;
window.lio.previewHoliday = previewHoliday;
// fire the bind celebration without binding a policy (sleigh / confetti / coins, depending on the holiday)
window.lio.celebrate = () => celebrate('bind');

export enum ErrorCode {
  Unknown,
  NotFound,
  DataConflict,
  DataNotProcessable,
  BadRequest,
  Forbidden,
}

export type ErrorResult = {
  code: ErrorCode;
  message: string;
  sessionId?: string;
  validationDetails?: Record<string, unknown>;
};

/**
 * @deprecated The method should not be used
 */
const client = async (endpoint: string, customConfig = {}) => {
  const config = { method: 'GET', ...customConfig };
  const res = await window.fetch(`${process.env.REACT_APP_API_URL}/${endpoint}`, config);

  if (res.status === 204) return { res, data: undefined };

  const data = await res.json();
  if (res.ok) return { res, data };
  return Promise.reject(data);
};

export default client;
